import { useEffect } from 'react';

import styled from 'styled-components';

import {
  Button,
  Flex,
  Form,
  Input,
  type JoiForm,
  PhoneNumberInput,
  Tooltip,
  useStylesFor as stylesFor,
  useJoiForm,
} from '@sine/sine-ui-components';

import type { InitialInvitationValues } from '../../Invitations.types';
import { inviteeSchema } from '../../invitations.schemas';

interface InviteeFormProps {
  id: string;
  formsRef: React.MutableRefObject<Record<string, JoiForm<InitialInvitationValues['invitee']>>>;
  defaultCountryCode?: string;
  addEmail: VoidFunction;
  removeEmail: VoidFunction;
  onDelete?: VoidFunction;
  onFormChange: (form: JoiForm<InitialInvitationValues['invitee']>) => void;
  initialValues?: InitialInvitationValues['invitee'];
}

const DeleteButtonWrapper = styled.div`
  margin-top: 36px;
`;

const DeleteButton = styled(Button)`
  ${stylesFor('tablet').andSmaller`
    margin: 0 auto;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    justify-self: center;
    align-self: center;

    &::after {
      content: 'Delete';
      display: inline;
      text-transform: capitalize;
      padding: 0 0 0 4px;
    }
  `}
`;

export function InviteeForm({
  defaultCountryCode,
  initialValues,
  addEmail,
  onDelete,
  removeEmail,
  onFormChange,
  formsRef,
  id,
}: InviteeFormProps) {
  const form = useJoiForm<InitialInvitationValues['invitee']>(inviteeSchema, initialValues!);

  useEffect(() => {
    onFormChange(form);
  }, []);

  useEffect(() => {
    if (form.values.email && form.values.email !== '') {
      addEmail();
    } else {
      removeEmail();
    }
  }, [form.values.email]);

  formsRef.current[id] = form;

  return (
    <Form values={form.values} errors={form.errors} onFieldChange={form.onChange}>
      <Flex gap={2} alignItems='flex-start' data-st-ext='invitee-row'>
        <Flex grow={1} direction='column' alignItems='stretch'>
          <Input name='firstName' label='First Name' data-st-ext='first-name' autoFocus noSpaceReservation />
        </Flex>
        <Flex grow={1} direction='column' alignItems='stretch'>
          <Input name='lastName' label='Last Name' data-st-ext='last-name' noSpaceReservation />
        </Flex>
        <Flex grow={1} direction='column' alignItems='stretch'>
          <Input name='email' label='Email' data-st-ext='email' noSpaceReservation />
        </Flex>

        <Flex grow={1} direction='column' alignItems='stretch'>
          <PhoneNumberInput
            name='mobile'
            label='Mobile'
            data-st-ext='mobile'
            optional
            // TODO: Fix type/UIC?
            defaultCountryCode={defaultCountryCode as any}
            noSpaceReservation
          />
        </Flex>

        {onDelete && (
          <Flex grow={0} direction='column' alignItems='stretch'>
            <DeleteButtonWrapper>
              <Tooltip content='Delete'>
                <DeleteButton
                  variant='tertiary'
                  iconOnly
                  icon='delete'
                  onClick={onDelete}
                  data-st-ext='delete-invitee'
                />
              </Tooltip>
            </DeleteButtonWrapper>
          </Flex>
        )}
      </Flex>
    </Form>
  );
}
